import {ProductLikeDefinition, UNASSIGNED} from '@api/graphql'
import {CatalogItemsCardViewStore, GroupByDropDownMenuSource, GroupSwatchesManagementPanelStore} from '@modules'
import {GroupByStore, site, OFFLINE_ID, ICatalogFieldConfig, ICustomGroupOrder, defaultGroupBySettings, GroupTitleConfig, ui, useCatalog, onSiteError, GroupByGroupStore} from '@stores'
import {action, computed, observable, ObservableMap, _, React, jsonComputedOptions, combineArraysRecursively, jsondiff, stringify, pluralize} from '@api'
import {CentricId, HitsResult, IAggregationResult, ICompositeBucketResult, ICompositeResult, MAX_QUERY_SIZE} from '@stores/elasticsearch'
import {IPlanCollection, IPlanDefinition, IPlanSubCategory, VLP_SUBCATEGORIES} from '@stores/vlp/store/VLPHierarchyStore'
import {tryParseJson, removeUndefinedsNested} from '@utils'
import {runInAction, toJS} from 'mobx'
import {computedFn} from 'mobx-utils'


export const VLP_FOLDER_GROUP = 'VLP_FOLDER'
export const VLP_PLAN_GROUP = 'VLP_PLAN'
export const VLP_COLLECTION_GROUP = 'VLP_COLLECTION'

export type StackView = 'collapsed' | 'spread' | 'grid' | 'expanded'

export interface IGroupByGroupingLocalStorageSettings {
  byGroup: { [groupName: string]: Partial<IIndividualGroupSettings> }

}

export interface IFilterableGroupResult/* extends ICompositeBucketResult*/ {
  key: { [group: string]: any }
  doc_count: number

  [agg: string]: ICompositeResult | IAggregationResult
  esItem?: {hits: HitsResult}
}

export interface IIndividualGroupSettings {
  childColumns: number,
  columnView: boolean,
  fullWidth: boolean,
  singleLine: boolean,
  stackView: StackView,
  orientation: GroupOrientation
  fullHeight: boolean
  expanded: boolean,
  showOnlyOfflineItems?: boolean
  showOnlyOnlineItems?: boolean
  infoPanelActive: boolean
  levelOfDetail?: number
  perspective?: string
  hideSwatches?: boolean

  /**
   * So the user can turn on a single aggregation in a group without changing any other part of the page
   */
  forceShowInlineAggregationDetails?: boolean
}

export interface GroupSwatchSettings {
  field?: string
  type?: 'swatches' | 'hero-cards'
  hero?: {
    columns?: string
  }
}

/**
 * Each individual group instance (something like material: Gold, category: Necklaces)
 * In any given unique group the user can change various settings like 'level of detail'. is it expanded, are we only viewing Pending Items, etc
 */
export interface IGroupByStoreNode {
  store?: GroupByGroupStore
  productStore?: CatalogItemsCardViewStore //| MasonryCardPanelStore
  definition: ProductLikeDefinition & { keys: { [key: string]: any }, path: { [key: string]: any }, aggs?: { ids: { buckets: Array<{ key: string }> } } }
  renderDimensions?: { width: number, height: number }
}

enum ORDER {
  ASC = 'asc',
  DESC = 'desc',
}

export interface IGroupOrderBy {
  field: '_key' | '_count' | string,
  order: 'asc' | 'desc',
  /** For fields like name that also have a custom grouping, user can toggle between alphabetical and custom sort */
  custom?: boolean

  nestedField?: string
}

export type CatalogLevelOfDetail = 0 | 1

/**
 * Shared data for tracking information for a given group (tags, some attribute, etc)
 * This is separate from the state for an individual group on screen and exists 1-1 with groupable fields
 */
export class GroupByGrouping {
  swatchManagementStore: GroupSwatchesManagementPanelStore
  @computed get fieldDepth() {
    const {catalog, fieldDefinition} = this
    if (!fieldDefinition) return 1

    let parentField = fieldDefinition.field.substring(0, fieldDefinition.field.lastIndexOf('.'))
    let depth = 1

    do {
      const parentFK = catalog.findFieldInTree(parentField, true)
      if (parentFK) {
        depth++
        parentField = parentFK.field.substring(0, parentFK.field.lastIndexOf('.'))
      }
      else {
        parentField = ''
      }
    } while (parentField != '')

    return depth
  }

  get settingsForLevel() { return this.groupBy.settings.settingsForLevel[this.indexInHierarchy] ?? {subtitleFields: [], swatches: [], swatchSize: 32}}

  @computed get isFlat() {
    return this.groupBy.settings.groupView === 'flat' || !this.isSpec || this.groupDefinition?.allowCardMode === false || this.fieldDefinition?.renderer === 'text'
  }

  get groupBy() { return this.store}

  get catalog() { return this.groupBy.catalog}

  constructor(public groupName: string, public store: GroupByStore) {
    const {defaultSortField, indexInHierarchy, catalog} = this

    let orderByField

    if (indexInHierarchy != -1) {
      if (this.isCatalogItemsGrouping && orderByField == '_key') {
        orderByField = defaultSortField
      }
    } else {
      orderByField = defaultSortField
    }

    runInAction(`Setup order by (default or from query param)`, () => {
      this.orderBy.replace([
        {
          field: orderByField ?? defaultSortField,
          custom: this.groupName !== this.catalog.workspaces.workspaceConfig?.catalog?.index,
          order: 'asc'
        }
      ])

      // Parse settings from query params
      const {groupsOrdersParsed} = store
      if (groupsOrdersParsed[groupName]) {
        const orderBy = groupsOrdersParsed[groupName]
        if (!_.isEmpty(orderBy)) {
          if (jsondiff.diff(toJS(this.orderBy), orderBy)) {
            this.orderBy.replace(orderBy)
          }
        }
      }

      if (this.isCatalogItemsGrouping) {
        const f = _.first(this.orderBy)?.field
        if (f && !catalog._fieldByName[f]) {
          this.orderBy[0].field = _.first(catalog.catalogConfig.defaultSort)?.field ?? 'name'
        }
      }
    })
  }

  @computed get groupDefinition() {
    return this.fieldDefinition?.group
  }

  @computed get allowCardMode() {
    return this.groupDefinition?.allowCardMode !== false && this.isSpec && this.fieldDefinition?.renderer !== 'text' && !this.catalog.isDataTableView
  }

  @computed get isSpec() {
    return this.fieldDefinition?.esIndex != null || this.fieldDefinition?.showInSpecGroup
  }

  @computed get associatedWorkspace() {
    const {ancestorForeignKey, catalog} = this

    return ancestorForeignKey?.esIndex ? catalog.workspaceByIndex[ancestorForeignKey.esIndex] : null
  }

  @computed get associatedLibrary() {
    const {esIndex, fieldName, groupName, catalog} = this, {libraries} = catalog
    if (!esIndex) return null

    return libraries.find(l => l.index === esIndex && (l.assignTo === fieldName || l.assignTo2?.field === fieldName))
  }

  @computed get sectionConfig() {
    return this.catalog.sectionConfigFor(this.fieldDefinition?.section)
  }

  @computed get isNestedField() {
    return this.sectionConfig?.path != null
  }

  @computed get isDynamicNestedField() {
    return this.sectionConfig?.path != null && this.fieldDefinition?.section == this.sectionConfig.path
  }

  @computed get isInHierarchy() {
    return this.indexInHierarchy !== -1
  }

  @computed get indexInHierarchy() {
    return this.store.hierarchy.indexOf(this.groupName)
  }

  @computed get hierarchyTypeGroup() {
    const {groupName} = this
    return groupName === VLP_PLAN_GROUP || groupName === VLP_COLLECTION_GROUP || groupName.startsWith(VLP_FOLDER_GROUP)
  }

  @computed get hierarchyType() {
    return this.catalog.vlp.hierarchy.parseHierarchyGroup(this.groupName)
  }

  get isOfflineGroup() { return this.groupName == OFFLINE_ID }

  @computed get editable() {
    const {catalog} = this
    return this.fieldDefinition && catalog.isFieldEditable(this.fieldDefinition?.field)
  }

  @computed get allowDragDropAssignLibrary() {
    const {groupName, catalog} = this,
      {workspaceConfig: {libraries}} = catalog,
      keys = groupName.split('.'),
      hasLibrary = libraries.find(l => l.name === keys[0])

    if (keys.length > 1 && !!hasLibrary?.index) {
      return false
    }
    return true
  }

  @computed get isAllowedDrag() {
    return !this.fieldDefinition?.def?.ref
  }

  @computed get isCatalogItemsGrouping() {
    return this.groupName === this.catalog.workspaces.workspaceConfig?.catalog?.index
  }

  @computed get vlpType() {
    const {groupName, catalog: {vlp}} = this
    if (!vlp.hierarchy.hasVlpHierarchy) return null

    try {
      return vlp.hierarchy.parseHierarchyGroup(groupName)
    } catch (err) {
      onSiteError(err)
      return null
    }
  }

  get vlp() { return this.catalog.vlp}

  @computed get itemCount() {
    const {catalog, vlp, groupName, fieldDefinition, sectionConfig, store: {counts}, isCatalogItemsGrouping, vlpType} = this,
      {workspaceConfig: {seasonIncludedPlans}, seasonSelector, settings: {groupBy: {showEmptyGroups}}} = catalog,
      {hierarchy: {offlineItemsByVlpHierarchyId}} = vlp

    let r : Array<any>

    if (vlpType) {
      const {type, folderDepth} = vlpType
      switch (type) {
        case 'plan':
          r = (vlp.hierarchy?.plans?.plans ?? []).filter(p => seasonIncludedPlans?.length ? seasonIncludedPlans.includes(p.name) : p)

          if (!showEmptyGroups) {
            const counts : Record<CentricId, number> = _.get(seasonSelector, '_counts.plans')
            if (!_.isEmpty(counts)) {
              r = r.filter(v => counts[v.exId] || !_.isEmpty(offlineItemsByVlpHierarchyId[v.exId]))
            }
          }
          break

        case 'folder':
          const {hierarchy: {selectedFolders, pathToFolder, leafFolderLevel}} = vlp,
            _selectedFolders = selectedFolders.map(s => s.exId)

          r = (vlp.hierarchy.folderByDepthForSelection[_.last(groupName.split('_'))] ?? []).reduce((acc: Array<Array<string>>, folder) => {
            if (!_selectedFolders.length || _selectedFolders.includes(pathToFolder(folder.exId)[folder.level > leafFolderLevel ? (leafFolderLevel - 1) : folder.level - 1])) {
              acc.push([folder.exId])
            }
            return acc
          }, [])

          if (!showEmptyGroups) {
            const counts: Record<CentricId, number> = _.get(seasonSelector, '_counts.folders')
            if (!_.isEmpty(counts)) {
              r = r.filter(v => v.some(id => counts[id] || !_.isEmpty(offlineItemsByVlpHierarchyId[id])))
            }
          }
          break

        case 'collection':
          r = vlp.hierarchy.allAvailableCollections
          if (!showEmptyGroups) {
            const counts: Record<CentricId, number> = _.get(seasonSelector, '_counts.collections')
            if (!_.isEmpty(counts)) {
              r = r.filter(v => counts[v.id] || !_.isEmpty(offlineItemsByVlpHierarchyId[v.id]))
            }
          }
          break
      }

      return r?.length ?? 0
    }
    const countVal = sectionConfig?.path ? counts[sectionConfig.path] ? counts[sectionConfig.path][groupName] : counts[groupName] : counts[groupName]

    if (isCatalogItemsGrouping) {
      return catalog.itemCount
    }

    if (fieldDefinition?.agg == 'exists') {
      return countVal?.doc_count == 0 ? 0 : countVal?.doc_count - countVal?.items?.doc_count % countVal?.doc_count == 0 ? 1 : 2
    }

    return countVal?.value
  }

  //@observable itemCount: number = null

  @computed get esIndex() { return this.fieldDefinition?.esIndex ?? this.parentField?.esIndex}

  @computed get parentField() {
    const {fieldDefinition, groupName, catalog} = this
    if (fieldDefinition) {
      let parentPath = groupName.substring(0, groupName.lastIndexOf('.'))
      while (parentPath) {
        const f = catalog._fieldByName[parentPath]
        if (f) return f
        parentPath = parentPath.substring(0, parentPath.lastIndexOf('.'))
      }
    }
  }

  @computed get isUnassigned() { return this.groupName == UNASSIGNED }

  @computed get isCustomAttribute() { return this.def != null && this.fieldName.indexOf('attributes.') != -1}

  @computed get filter() { return this.catalog.filter.state.groups[this.groupName]}

  @computed get defaultSortField() {
//    return this.isProductView ? (_.get(_.first(catalog.boardConfig.catalog.defaultSort), 'field') ?? 'name') : '_key'
    const {catalog: {workspaceConfig}} = this
    return !this.isCatalogItemsGrouping ? '_key' : (workspaceConfig.catalog.initialSort ?? 'name')
  }

  @computed get titleAndCount() {
    return this.groupBy.getTitleWithCount(this.fieldName)
  }

  products = observable.map<string, ObservableMap<string, ProductLikeDefinition>>({}, {deep: false})

  @computed get compositeFields() {
    if (!this.fieldDefinition) return []

    const {groupName, fieldDefinition, subtitleFields, fieldDefinition: {group}} = this

    return [fieldDefinition?.field, ...[group?.title?.field, ...subtitleFields.filter(f => !f.valueFor).map(f => f.field) || []].filter(v => v && v != groupName)].filter(_.identity)
  }

  //get orderBy() : Array<IGroupOrderBy> { return this.settings.get('orderBy') } //@observable orderBy = observable.array<IGroupOrderBy>()
  @observable orderBy = observable.array<IGroupOrderBy>()

  @computed get firstOrderBy() { return _.first(this.orderBy) }

  @computed get customSort(): Array<ICustomGroupOrder> {
    const {orderBy, catalog} = this

    if (orderBy.every(ob => !ob.custom)) return undefined

    const fieldsWithCustomSorts = orderBy.map(ob => {
      if (ob.custom && ob.field != '_count') {
        const customOrder = catalog._fieldByName[ob.field]?.sort?.custom?._key
        return customOrder ? {...customOrder, field: ob.field} : undefined
      }
    }).filter(_.identity)

    return _.isEmpty(fieldsWithCustomSorts) ? undefined : fieldsWithCustomSorts
  }

  @computed get firstCustomSort(): ICustomGroupOrder {
    const {customSort} = this
    return customSort ? _.first(customSort) : undefined
  }

  @computed get groupOfflineItems() {
    return this.catalog.groupOfflineItems(this.fieldName, false)
  }

  @computed get offlineUnassigned() { return this.offlineUnassignedItems.length }

  @computed get offlineUnassignedItems() {
    const {vlpType, fieldName, catalog: {filteredOfflineItems}} = this

    if (vlpType?.type) {
      const {type, folderDepth} = vlpType
      switch (vlpType.type) {
        case 'plan':
        case 'collection':
          return filteredOfflineItems.filter(v => !_.has(v, type))

        case 'folder':
          const r = filteredOfflineItems.filter(item => {
            const v = _.get(item, VLP_SUBCATEGORIES)
            return !v || folderDepth >= v.length
          })

          return r
      }
    } else {
      return filteredOfflineItems.filter(v => _.get(v, fieldName) == null)
    }

    return []
  }

  @computed get orderBys(): Array<IGroupOrderBy> {
    const {compositeFields, orderBy, groupName} = this,
      firstOrderBy = _.first(orderBy)

    return _.isEmpty(compositeFields) ? orderBy :
           _.orderBy(
             compositeFields.map(field => ({field, custom: true, order: firstOrderBy?.field == field || firstOrderBy?.field == '_key' ? firstOrderBy?.order : 'asc'})),
             f => f.field != firstOrderBy?.field && !(firstOrderBy?.field == '_key' && f.field == groupName)
           )
  }

  /**
   * Combination of groupBy field, subtitle fields, and any ancestors
   */
  @computed get fieldsInUse(): Set<string> {
    const {fieldDefinition, parentGrouping} = this,
      myFields = [fieldDefinition?.field, ...(fieldDefinition?.group?.subtitle ?? []).map(f => f.field)].filter(_.identity)

    return new Set<string>([...myFields, ...(parentGrouping ? Array.from(parentGrouping.fieldsInUse.keys()) : [])] as string[])
  }

  @computed get parentGrouping() {
    const {indexInHierarchy, store: {hierarchyGroups}} = this

    return indexInHierarchy === 0 ? null : hierarchyGroups[indexInHierarchy - 1]
  }

  /*
  A dynamic longer title for the group that includes user selected subtitle fields like:
  Article + Persona
   */
  @computed get fullTitle() {
    const {title, mySubtitleFieldsNonHierarchy, catalog} = this
    return `${title}${mySubtitleFieldsNonHierarchy.map(f => ` + ${catalog.labelForFieldName_currentCatalog(f.field)}`).join('')}`
  }

  /**
   * Title of only the group itself, not including any subtitles
   */
  @computed get groupTitle() {
    return this.title
  }

  get strings() { return this.catalog.strings}

  @computed get title() {
    const {strings, groupName, fieldDefinition, associatedWorkspace, catalog, catalog:{catalogConfig}} = this,
      prefix = associatedWorkspace && associatedWorkspace.catalog?.index != fieldDefinition?.esIndex ? `${associatedWorkspace.title} > ` : '',
      suffix = ''

    if (this.groupName == OFFLINE_ID) return `${prefix}${strings.offlineItems}${suffix}`

    return `${prefix}${groupName == catalogConfig.index ? pluralize(catalog.catalogItemName) : catalog.labelForField(fieldDefinition, groupName, 'singular')}${suffix}`
  }

  /**
   * If the grouping in config has subtitle preset always include those
   * The user can also add and remove subtitle fields themselves
   * Todo - the my groups should include the preconfigured subtitles in config each time we are added to the hierarchy
   * Test this in ASICS + Merch Product

   */
  @computed get mySubtitleFields(): Array<Partial<GroupTitleConfig>> {
    const {isCatalogItemsGrouping, fieldDefinition, groupName, settingsForLevel: {subtitleFields}} = this
    return isCatalogItemsGrouping
           ? []
           : _.uniqBy(
        [
          ...(fieldDefinition?.group?.subtitle ?? []),
          ...subtitleFields.filter(f => f != groupName).map(f => ({field: f}))
        ], f => f.field)
  }

  @computed get mySubtitleFieldsNonHierarchy(): Array<Partial<GroupTitleConfig>> {
    const {mySubtitleFields, vlp} = this
    return mySubtitleFields.filter(f => !vlp.hierarchy.parseHierarchyGroup(f.field))
  }

  @computed get subtitleFields(): Array<Partial<GroupTitleConfig>> {
    const {parentGrouping, mySubtitleFields} = this

    const subtitleFields = mySubtitleFields ?? [],
      parentFieldsInUse = parentGrouping?.fieldsInUse ?? new Set<string>()

    //  Don't show same subtitles as ancestor groups would already have
    return !parentGrouping ? subtitleFields
                           : subtitleFields.filter(f => !parentFieldsInUse.has(f.field))
  }

  @action toggleInHierarchy = (level?: number, source?: GroupByDropDownMenuSource) => {
    this.groupBy.onClickGroupBy_modifyHierarchy(this.groupName, level ?? this.level, source)
  }

  @action toggleSubtitleField = (field: string) => {
    const {groupBy} = this
    if (this.isCatalogItemsGrouping && groupBy.isGroupedView) {
      _.last(groupBy.hierarchyGroups).toggleSubtitleField(field)
    } else {
      if (field === this.fieldDefinition?.field) return

      const {settingsForLevel, settingsForLevel: {subtitleFields}} = this
      if (subtitleFields.some(f => f === field)) {
        settingsForLevel.subtitleFields = subtitleFields.filter(f => f != field)
      } else {
        settingsForLevel.subtitleFields = [...subtitleFields, field]
      }

      settingsForLevel.subtitleFields = _.takeRight(settingsForLevel.subtitleFields, ui.settings.maxSubtitlesPerGroup)
    }
  }

  @computed get sortByFields() {
    const {fieldCustomSorts, fieldName, subtitleFields, catalog, catalog: {labelForFieldName_currentCatalog}} = this
    return [
      {
        field: '_count',
        label: '# of Items'
      },
      {
        field: '_key',
        label: fieldCustomSorts ? labelForFieldName_currentCatalog(fieldName, 'plural') : 'Name'
      },
      ...(subtitleFields.map(({field}) => ({field, label: labelForFieldName_currentCatalog(field)})) ?? [])
    ].filter(_.identity)
  }

  @computed get customOrders() {
    const {orderBys, fieldDefinition, catalog, store: {groupingByName}} = this
    return orderBys.filter(ob => ob.custom).map(({field, order}) => {
      const fieldDef = catalog._fieldByName[field]

      let custom = (fieldDefinition?.sort?.custom || {})[field]
      if (!custom && field != '_count') {
        custom = fieldDef?.sort?.custom?._key
      }

      if (!custom && fieldDef?.agg !== 'exists') return null

      return ({
        field,
        fieldDef,
        order,
        esFieldRoot: (groupingByName.get(field) ?? {esFieldRoot: field}).esFieldRoot,
        customSort: !custom ? undefined : order == 'desc' ? (custom.orderDesc ?? _.reverse(_.clone(custom.order))) : custom.order
      })
    }).filter(_.identity)
  }

  @computed get fieldCustomSorts() {
    const {fieldDefinition} = this
    return fieldDefinition?.sort?.custom
  }

  @action toggle2DColumnView = () => {
    this.settingsForLevel.twoDColumnView = !this.settingsForLevel.twoDColumnView
  }

  @action toggleSortField = (field: string, order?: 'asc' | 'desc', custom = true) => (e?: React.MouseEvent) => {
    const {isCatalogItemsGrouping, orderBy, groupName} = this

    console.log(`toggleSortField`, {groupName, field, order, custom})

    const index = orderBy.findIndex(f => f.field == field)

    if (isCatalogItemsGrouping) {
      if (index == -1) {
        orderBy.push({field, custom, order: order ?? 'asc'})
      } else {
        if (orderBy.length > 1) {
          orderBy.splice(index, 1)
        } else {
          orderBy[index].order = order ?? (orderBy[index].order === 'asc' ? 'desc' : 'asc')
          orderBy[index].custom = custom
        }
      }
    } else {
      if (index == -1) {
        orderBy.replace([{field, custom, order: order ?? 'asc'}])
      } else {
        orderBy[index].order = order ?? (orderBy[index].order === 'asc' ? 'desc' : 'asc')
        orderBy[index].custom = custom
      }
    }
  }

  @action onRemoveSubtitleField = (field: string) => (e: React.MouseEvent) => {
    const {fieldDefinition: {group}, settingsForLevel} = this
    settingsForLevel.subtitleFields = settingsForLevel.subtitleFields.filter(f => f !== field)
  }

  @computed get fieldName() {
    const {def, groupName} = this
    return def?.name ?? groupName
  }

  @computed get fieldDefinition(): ICatalogFieldConfig {
    const {fieldName, catalog: {_fieldByName}} = this

    return _fieldByName[fieldName]
  }

  @computed get isEnum() {
    return this.fieldDefinition?.def?.type === 'Enum' || this.fieldDefinition?.def?.type === 'EnumList'
  }

  @computed get def() {
    const {groupName} = this,
      {catalog: {caDefByName}} = this

    return caDefByName[groupName]
  }

  get esField() {return this.store.esFieldFor(this.groupName)}

  get esFieldRoot() {
    return this.store.esFieldRootFor(this.groupName)
  }

  get esOrder() {
    const {orderBy} = this
    return {
      //order: {[`${field}${fieldByName[field]?.keyword ? '.keyword' : ''}`]: order == 'ASC' ? 'asc' : 'desc'},
      order: orderBy.map(({field, order}) => ({[field == 'name' ? '_key' : field === '_key' ? field : '_count']: order.toLowerCase() == 'asc' ? 'asc' : 'desc'}))
    }
  }

  /**
   * Does NOT include unassigned as a group
   */
  @computed get size() {
    const {products} = this
    let size = this.products.size
    return products.has(UNASSIGNED) ? size - 1 : size
  }

  nodes = observable.map<string, IGroupByStoreNode>({})

  @computed get groupsInOrder(): Array<IGroupByStoreNode> {
    const {groupBy, nodes} = this,
      groups = Array.from(nodes.values()).filter(n => n.store)

    return _.orderBy(groups, node => {
      let {store: {parentStore, props: {index, level}}} = node,
        r = Math.pow(100, groupBy.hierarchy.length - level) * (index + 1)

      while (parentStore != null) {
        let {props: {index, level}} = parentStore
        parentStore = parentStore.parentStore

        r += Math.pow(100, groupBy.hierarchy.length - level) * (index + 1)
      }

      //console.log(store.pathIncludingSelf, r)
      return r
    })
  }

  @computed get focusedGroup(): IGroupByStoreNode {
    const {catalog: {focusedCatalogItemId} } = this
    if (!focusedCatalogItemId) return null

    const groupId = focusedCatalogItemId.substring(0, focusedCatalogItemId.lastIndexOf('.'))
    return this.nodes.get(groupId)
  }

  @computed get focusedCatalogItemIndex() {
    const {focusedGroup, catalog: {focusedCatalogItemId, parseIdFromPath}} = this
    return !focusedGroup?.productStore ? -1 : focusedGroup.productStore.itemPositionByID[parseIdFromPath(focusedCatalogItemId)]
  }

  @computed get lastCatalogItemIdInGroup() {
    return _.last(this.focusedGroup?.productStore?.catalogItemIdsArray)
  }

  /**
   *  Settings for the level of the hierarchy
   * May be overridden at the specific group level
   * **/
  get settings() {
    const {groupBy} = this
    return {
      ...groupBy.individualGroupSettings, ...(groupBy.settings.settingsForLevel[this.indexInHierarchy] ?? {})
    }
  }

  /**
   * Per group settings, key is a path to an individual grouping node
   */
  settingsByGroup = observable.map<string, Partial<IIndividualGroupSettings>>({})

  /**
   * Only return non-default values to reduce what needs to be stored on the query string
   */
  @computed get settingsNotDefault() {
    const r = {
        byGroup: {}
      },
      {settingsByGroup} = this,
      {individualGroupSettings} = defaultGroupBySettings

    Array.from(settingsByGroup.keys()).forEach(k => {
      const setting: Partial<IIndividualGroupSettings> = {},
        v = settingsByGroup.get(k)

      _.keys(individualGroupSettings).forEach(settingKey => {
        if (v[settingKey] !== undefined && v[settingKey] != individualGroupSettings[settingKey]) {
          setting[settingKey] = v[settingKey]
        }
      })

      if (!_.isEmpty(setting)) {
        r.byGroup[k] = setting
      }
    })

    return r
  }

  @computed get expanded() {
    const {settingsByGroup} = this
    return new Set<string>(Array.from(settingsByGroup.entries()).filter(([k, {expanded}]) => expanded).map(([k]) => k)) as ReadonlySet<string>
  }

  /**
   * As we search we may invalidate items that were marked as expanded before, but we want to get them back as we correct our search
   */
  @computed get validExpanded() {
    const {expanded, nodes} = this
    return new Set(Array.from(expanded.keys()).filter(k => nodes.has(k) || k.endsWith('_unassigned')))
  }

  pathLookupObjFor = (group: string, path: Array<string>) => {
    return Object.assign(
      !group ? {} : (tryParseJson(group) ?? group),
      (path ?? []).reduce((acc, v) => Object.assign(acc, JSON.parse(v)), {})
    )
  }

  /**
   * Note that the group here may be an attribute value which can have a . in them
   * @param group
   * @param path
   */
  pathLookup = (group: string, path: Array<string>) => {
    //if (group && !_.isString(group)) { throw new Error(`${group} must be a string`)}

    //const lookup = _.trim(`${path.join(GROUP_BY_PATH_SEPARATOR)}${group != null ? `${GROUP_BY_PATH_SEPARATOR}${group}` : ''}`, GROUP_BY_PATH_SEPARATOR)
    const lookupObj = this.pathLookupObjFor(!group || _.isString(group) ? group : JSON.stringify(group), path)
    const lookup = stringify(lookupObj)

    return lookup
  }

  @computed get level() {
    const {groupName, store: {hierarchy}} = this
    return hierarchy.indexOf(groupName)
  }

  @computed get isLeaf() {
    const {level, store: {hierarchy}} = this
    return level + 1 >= hierarchy.length
  }

  @computed get isExpandedAll() {
    const {expanded, nodes, level, store: {settings: {expandAllLevel}}, groupBy} = this

    if (expandAllLevel > level) { return true }

    const isEveryNodeExpanded = nodes.size > 0 && Array.from(nodes.keys()).every(k => expanded.has(k))

    return isEveryNodeExpanded || groupBy.pivotView  // We can have nodes marked as expanded that don't currently exist in the result set, be wary of just checking sizes
  }

  @computed get isCollapsedAll() {
    const {expanded} = this
    return expanded.size == 0 && this.store.settings.expandAllLevel <= 0
  }

  @computed get expandedSize() {
    const {validExpanded} = this
    return validExpanded.size
  }

  isExpanded = computedFn((group?: string, path: string[] = []) => {
    const {expanded, isExpandedAll, level} = this

    if (isExpandedAll) return true

    if (!expanded.size) return false

    const lookup = this.pathLookup(group, path)

    return expanded.has(lookup)
  })

  isShowQrCode = (group: string, path: string[] = []) => {
    const {showQrCode} = this
    const lookup = this.pathLookup(group, path)

    return showQrCode.has(lookup) ? showQrCode.get(lookup) : null
  }

  @action setExpanded = (id: string, val: boolean, path: string[] = []) => {
    // console.log(`setExpanded`, {id, val, path})
    const {settingsByGroup, expanded} = this
    const lookup = this.pathLookup(id, path)

    if (!settingsByGroup.has(lookup)) {
      settingsByGroup.set(lookup, {})
    }
    settingsByGroup.get(lookup).expanded = val
  }

  @action setLevelOfDetail = (id: string, val: number, path: string[] = []) => {
    const {settingsByGroup} = this
    const lookup = this.pathLookup(id, path)
    if (!settingsByGroup.has(lookup)) {
      settingsByGroup.set(lookup, {})
    }
    settingsByGroup.get(lookup).levelOfDetail = val
  }

  @action collapseAll = () => {
    const {store, expanded, nodes, level, setExpanded} = this
    Array.from(expanded.keys()).forEach(k => setExpanded(k, false))
    store.settings.expandAllLevel = Math.max(0, level - 1)
  }

  @action expandAll = () => {
    const {store, nodes, level, settingsByGroup} = this
    Array.from(nodes.keys()).forEach(k => {
      if (settingsByGroup.has(k)) {
        settingsByGroup.get(k).expanded = true
      } else {
        settingsByGroup.set(k, {expanded: true})
      }
    })
    store.settings.expandAllLevel = Math.max(level + 1, store.settings.expandAllLevel)
  }

  @action toggleExpandAll = () => {
    const {collapseAll, expanded, expandAll} = this
    expanded.size ? collapseAll() : expandAll()
  }

  @action toggleExpanded = (id, path: string[] = []) => {
    const {expanded, level, store, setExpanded, store: {settings: {expandAllLevel}}} = this
    const lookup = this.pathLookup(id, path)

    if (expanded.has(lookup)) {
      // Collapsing...
      setExpanded(lookup, false)
      if (this.level > 0) {
        if (expandAllLevel > this.level) {
          store.settings.expandAllLevel = this.level
        }
      } else {
        store.settings.expandAllLevel = 0
      }
    } else {
      setExpanded(lookup, true)
    }
  }

  showQrCode = observable.map<string, boolean>({}, {deep: false})
  @action toggleShowQrCode = (id, path: string[] = []) => {
    const {showQrCode} = this
    const lookup = this.pathLookup(id, path)

    showQrCode.set(lookup, showQrCode.has(lookup) ? !showQrCode.get(lookup) : true)
  }

  selected = observable.set<string>()

  isSelected = (group: string, path: string[] = []) => {
    const {selected} = this
    const lookup = this.pathLookup(group, path)
    return selected.has(lookup)
  }

  @action toggleSelected = (id, path: string[] = []) => {
    const {selected} = this
    const lookup = this.pathLookup(id, path)
    selected.has(lookup) ? selected.delete(lookup) : selected.add(lookup)
  }

  @action setSelected = (id, path: string[] = [], v = true) => {
    const {selected} = this
    const lookup = this.pathLookup(id, path)
    v ? selected.add(lookup) : selected.delete(lookup)
  }

  @observable focused: string = null
  onClickGroup = (id: string, {ids, path, shiftKey, metaKey, ctrlKey, buttons}: { ids?: string[], path: string[], shiftKey?: boolean; buttons: number; metaKey?: boolean; altKey?: boolean, ctrlKey?: boolean }) => {
    const {selected, focused, catalog: {settings}} = this

    if (ctrlKey || metaKey) {
      // Add or remove the item clicked
      if (selected.has(id)) {
        selected.delete(id)
      } else {
        selected.add(id)
      }
      this.focused = selected.size > 0 ? id : null
    } else if (shiftKey && focused && id) {
      //this.focused = id
      this.selectRange(focused, id, ids)
    } else {
      if (site.touchDevice) {
        selected.has(id) ? selected.delete(id) : selected.add(id)
      } else {
        if (selected.size != 1 || !selected.has(id)) {
          selected.clear()
          selected.add(id)
        } else if (selected.size == 1 && selected.has(id)) {
          selected.delete(id)
        }
      }

      this.focused = selected.size > 0 ? id : null
    }

    settings.aggregateDetailsGroupPath = selected.has(id) ? [path] : []
  }

  @action selectRange = (fromId: string, toId: string, objects: Array<string>) => {
    const {selected} = this
    const fromIndex = objects.findIndex(v => v == fromId)
    const toIndex = objects.findIndex(v => v == toId)

    selected.clear()
    for (let i = Math.min(fromIndex, toIndex); i <= Math.max(fromIndex, toIndex); i++) {
      selected.add(objects[i])
    }
  }

  @action unregisterNodes = (path: string[]) => {
    // console.log(`unregisterNodes()`, this.groupName, path)
    const {nodes} = this
    if (path.length == 0) {
      nodes.clear()
    } else {
      const pathPrefix = this.pathLookup(null, path)
      Array.from(nodes.keys()).filter(k => k.startsWith(pathPrefix)).forEach(k => nodes.delete(k))
    }
  }

  @action registerChildren = (childrenNodes: Array<Partial<{ key?: string, keys?: any, id?: string }>>, path: string[] = []) => {
    // console.log(`registerChildren()`, {groupName: this.groupName, path, childrenNodes})
    const {nodes, pathLookup} = this

    childrenNodes.forEach(node => {
      const key = pathLookup(node.key ?? node.id, path)

      if (!nodes.has(key)) {
        nodes.set(key, {
          definition: {...node, path},
          orientation: 'vertical'
        } as IGroupByStoreNode)
      } else {
        const existing = nodes.get(key)
        Object.assign(existing, {definition: {...node, path}})
      }
    })
  }

  expandedCounts = computedFn((path) => {
    const {expanded, nodes} = this
    if (!_.isEmpty(path)) {
      let lookup = this.pathLookup(null, path)
      const myNodes = Array.from(nodes.keys()).filter(k => k.startsWith(lookup))
      const expandedSize = myNodes.filter(k => expanded.has(k)).length

      return {expanded: expandedSize, unexpanded: myNodes.length - expandedSize}
    } else {
      return {expanded: expanded.size, unexpanded: nodes.size - expanded.size}
    }
  })

  /*saveToNode = ({path} : { path: string[] }, vals: Partial<IGroupByStoreNode>) => {
    const {nodes, pathLookup} = this
    const key = pathLookup('', path)
    nodes.set(key, Object.assign({}, nodes.get(key), vals))
  }*/

  @action collapseAllUnder = (path: string[] = []) => {
    const {expanded, level, childrenNodesOf, setExpanded, groupBy} = this,
      children = childrenNodesOf(path),
      toCollapse = children.filter(n => expanded.has(n[0])),
      {hierarchyGroups, settings} = groupBy

    if (!toCollapse.length) return toCollapse

    if (toCollapse.length == children.length && level + 1 < hierarchyGroups.length) {
      // Are all grandchildren collapsed?

      const g = hierarchyGroups[level + 1]
      const collapsedItems = _.flatMap(children.map(([, {store}]) => {
        return store && g.collapseAllUnder(store.pathIncludingSelfKeys)
      })).filter(_.identity)

      if (collapsedItems.length != 0) {
        return collapsedItems
      }
    }

    toCollapse.forEach(([k]) => setExpanded(k, false))
    // settings.expandAllLevel = settings.expandAllLevel === hierarchyGroups.length ? 0 : settings.expandAllLevel
    return toCollapse
  }

  @action expandAllUnder = (path: string[] = []) => {
    const {childrenNodesOf, level, expanded, setExpanded, groupBy} = this,
      children = childrenNodesOf(path),
      toExpand = children.filter(c => !expanded.has(c[0]))

    if (_.isEmpty(toExpand) && !_.isEmpty(children)) {
      // Expand all children groups
      if (this.level + 1 < groupBy.hierarchy.length) {
        const g = groupBy.hierarchyGroups[level + 1]
        const expanded = children.map(([, {store}]) => {
          return store && g.expandAllUnder(store.pathIncludingSelfKeys)
        })

        if (expanded.length) {
          return expanded
        }
      }
    }

    toExpand.forEach(([k]) => setExpanded(k, true))

    return toExpand
  }

  childrenNodesOf = computedFn((path: string[]) => {
    const {nodes} = this
    return Array.from(nodes.entries()).filter(([groupKey, {definition}]) => _.isEqual(path, definition.path))

    // const {nodes, pathLookupObjFor} = this,
    //   lookup = pathLookupObjFor(null, path),
    //   lookupKeys = _.keys(lookup)
    //
    //   return Array.from(nodes.entries()).filter(([groupKey, {definition: {keys}}]) => lookupKeys.every(k => _.get(keys, k) === _.get(lookup, k)))
  })

  @action setLevelOfDetailAllUnder = (path: string[] = [], toggleLevelOfDetail: any, levelOfDetail: number) => {
    const {childrenNodesOf, groupBy, level} = this,
      children = childrenNodesOf(path)

    if (!_.isEmpty(children)) {
      if (level + 1 < groupBy.hierarchy.length) {
        const g = groupBy.hierarchyGroups[level + 1]
        const detail = children.map(([, {store}]) => {
          return store && g.setLevelOfDetailAllUnder(store.pathIncludingSelfKeys, toggleLevelOfDetail, levelOfDetail)
        })

        if (detail.length) {
          detail.forEach(([k]) => this.setLevelOfDetail(k[0], levelOfDetail))
        }
      }
    } else if (level + 1 < groupBy.hierarchyGroups.length) {
      const g = groupBy.hierarchyGroups[level + 1]
      const detail = _.flatMap(children.map(([k, {store}]) => {
        return store && g.setLevelOfDetailAllUnder(store.pathIncludingSelfKeys, toggleLevelOfDetail, levelOfDetail)
      }))

      if (detail.length != 0) {
        detail.forEach(k => this.setLevelOfDetail(k[0], levelOfDetail))
      }
    }
    children.forEach(([k]) => this.setLevelOfDetail(k, levelOfDetail))
    return children
  }

  @observable orientation: GroupOrientation = 'horizontal'
  @action toggleOrientation = () => this.orientation = this.orientation == 'horizontal' ? 'vertical' : 'horizontal'

  @computed(jsonComputedOptions) get functionScoreTerm(): {} {
    //trace()
    const {customOrders, catalog: {catalogItemFilter}} = this

    if (!customOrders.length) return undefined
    // YFTS-6003 Added empty string in custom sorting with it's order before null so that "No Value" is before "Unassigned" in custom sort
    const keyCombinationsOrdered = combineArraysRecursively(
      customOrders.map(o =>
        o.fieldDef.agg === 'exists'
        ? (o.order == 'asc' ? [true, false] : [false, true])
        : [...(o.customSort ?? []), '', null])),
      functions = !keyCombinationsOrdered.length ? [
          // Does not work - trying to move '' group to end
          // {
          //   script_score: {
          //     script: `return 10000`
          //   }
          // },
          // {
          //   weight: 10000,
          //   filter: {bool: {must_not: {exists: {field: groupName}}}}
          // }
        ] :
        [
          {
            script_score: {
              script: `return ${keyCombinationsOrdered.length + 1}`
            }
          },
          ...keyCombinationsOrdered.map((keys, i) => {
            const filter = keys.filter(f => keys.length != 1 || f !== 'null').map((k, j) => {
              const {esFieldRoot, fieldDef} = customOrders[j],
                orFields = fieldDef?.orFields ?? []

              if (fieldDef?.agg === 'exists') {
                if (k === true || k === 'true') {
                  return {
                    bool: {
                      minimum_should_match: 1,
                      should: [esFieldRoot, ...orFields].map(f => ({
                        bool: {
                          minimum_should_match: 2,
                          should: [
                            {exists: {field: f}},
                            {bool: {must_not: [{term: {[f]: ''}}]}}
                          ]
                        }
                      }))
                    }
                  }
                } else {
                  return {
                    bool: {
                      minimum_should_match: 1 + orFields.length,
                      should: [esFieldRoot, ...orFields].map(f => ({
                        bool: {
                          minimum_should_match: 1,
                          should: [
                            {bool: {must_not: [{exists: {field: f}}]}},
                            {bool: {must: [{term: {[f]: ''}}]}}
                          ]
                        }
                      }))
                    }
                  }
                }
              } else {
                const term = ({match: {[customOrders[j].esFieldRoot]: k}})
                if (_.isEmpty(orFields)) return term
                return {
                  bool: {
                    minimum_should_match: 1,
                    should: [
                      term,
                      orFields.map(f => ({match: {[f]: k}}))
                    ]
                  }
                }
              }
            })

            if (filter.length) {
              return ({
                weight: i + 1,
                filter: {bool: {filter}}
              })
            }
          }).filter(_.identity)
        ]

    // console.log(keyCombinationsOrdered)
    return removeUndefinedsNested(
      {
        function_score: {
          query: _.isEmpty(catalogItemFilter.filter.query) ? undefined : {
            ...catalogItemFilter.filter.query
          },
          boost: 1,
          boost_mode: 'replace',
          score_mode: 'min',
          functions
        }
      })
  }

  @computed get showAddIcon() {
    const {catalog: {canCreateItem}, parentGrouping, fieldDefinition} = this
    return (parentGrouping?.showAddIcon ?? true) && fieldDefinition?.agg !== 'exists' && fieldDefinition?.group?.add !== false && canCreateItem
  }

  groupsClauseFor = ({itemsPerClick, timesClicked, groupCount, itemsPerGroup}: { itemsPerClick?: number, timesClicked?: number, groupCount?: number, itemsPerGroup?: number }) => {
    if (!this.fieldDefinition) return undefined

    const {catalog, esField, esFieldRoot, esOrder, fieldDefinition: {group, agg, orFields}, subtitleFields} = this,
      orderBy = _.first(this.orderBy)
    switch (agg) {
      case 'exists': {
        const existsClause = {
          filter: _.isEmpty(orFields)
                  ? {exists: {field: esFieldRoot}}
                  : {bool: {minimum_should_match: 1, should: [{exists: {field: esFieldRoot}}, ...orFields.map(field => ({exists: {field}}))]}}
        },
          existsField = catalog._fieldByName[esField]

        return existsField?.def?.type === 'String' ? {
          filter: {
            bool: {
              ...existsClause,
              must_not: {term: {[esFieldRoot]: ''}}
            }
          }
        } : existsClause
      }

      default: {
        //if (orderBy.field != 'name' && orderBy.field != '_count') return undefined

        const num_partitions = groupCount ? Math.ceil(groupCount / itemsPerClick) : 1
        return {
          terms: {
            field: esField,
            ...esOrder,
            // https://www.elastic.co/guide/en/elasticsearch/reference/current/search-aggregations-bucket-terms-aggregation.html#_filtering_values_with_partitions
            size: itemsPerClick,
            include: timesClicked && orderBy.field != 'name' ? {partition: Math.min(num_partitions, timesClicked), num_partitions} : undefined
          },
          aggs: {
            groupData: {top_hits: {size: itemsPerGroup ?? 1, _source: {includes: [`${esFieldRoot}`, group?.title?.field, ...subtitleFields.map(f => f.field)].filter(_.identity)}}},
            ...[group?.title].filter(_.identity).filter(v => v.field).reduce((acc, v) => {
              acc[v.field] = {terms: {field: catalog.esFieldFor(catalog.fieldByName(v.field))}}
              return acc
            }, {}),
            ...(subtitleFields || []).filter(v => !v.valueFor && v.field).reduce((acc, v) => {
              acc[v.field] = {terms: {field: catalog.esFieldFor(catalog.fieldByName(v.field))}}
              return acc
            }, {})

            // Below won't work, to do sorting of non group-ed fields we need to switch to a composite aggregation:  https://centric8.atlassian.net/browse/YFTS-4181
            /*  ...(orderBy.field !== 'name' && orderBy.field !== 'count'
                  ? {
                [orderBy.field]: { max: {field: orderBy.field}},
                  sorted: { bucket_sort: { sort: [{[orderBy.field]: { order: orderBy.order}}]}}
                }
              : {})*/
          }
        }
      }
    }
  }

  // @computed get compositeFields() {
  //   const {orderBy, groupName, customSort, esField, fieldDefinition: {group: {title, subtitle}}} = this,
  //     {esFieldFor, fieldByName, _fieldByName} = catalog,
  //     otherGroupedFields = [title, ...(subtitle || [])].filter(v => v && v.field != orderBy.field && v.field != groupName),
  //     orderField = _fieldByName[orderBy.field]
  //
  //   if ((orderBy.field != '_count') || otherGroupedFields.length) {
  //     return [
  //       orderField && orderField.field != groupName && orderField,
  //       esField,
  //       ...otherGroupedFields.map(v => v.field)
  //     ].filter(_.identity)
  //   }
  //   return []
  // }

  @computed get otherGroupedFields() {
    const {groupName, fieldDefinition, subtitleFields} = this,
      orderBy = _.first(this.orderBy)

    return [fieldDefinition?.group?.title, ...subtitleFields].filter(v => v && !v.valueFor && v.field && (!orderBy || (v.field != orderBy.field && v.field != groupName)))
  }

  compositeGroupsClauseFor = computedFn(({itemsPerClick, missing_bucket}: { itemsPerClick: number, missing_bucket?: boolean }) => {
    const {vlp, catalog, fieldDefinition} = this
    if (!fieldDefinition || !_.isNumber(catalog.onlineItemCount)) return undefined

    const {groupName, customSort, esField, otherGroupedFields, customOrders} = this,
      {esFieldFor, fieldByName, _fieldByName} = catalog,
      orderBy = _.first(this.orderBy)

    try {
      const orderFieldName = orderBy.field == '_count' ? '_key' : orderBy.field

      if ((orderFieldName != '_count') || otherGroupedFields.length) {
        // We can't rely on the compound query to do subdivision for subtitles and such as it prevents us from sorting by # of items in a group.
        // See https://discuss.elastic.co/t/composite-aggregation-order-by/139563/6
        const orderField = _fieldByName[orderFieldName],
          firstCustomSort = _.first(customSort)

        //console.log({orderField: orderBy.field, otherGroupedFields})
        //console.log({orderBy: toJS(orderBy), orderField: orderField?.field, customSort})
        return {
          composite: {
            /** Custom scoring only works on keys that we see in the aggs response so in order to support this we have to get ALL rows.  The number below could potentially be
             * # of merch products * # of seasons * # of Item Codes * # of  */
            // We can not know how many buckets there might be...
            // https://discuss.elastic.co/t/composite-aggregation-buckets-total-count/137156

            //In case of TagList the count is more than catalog.itemCount and we cannot judge the exact count in that case. Hence 5000 + catalog.itemCount
            size: _.isEmpty(customOrders) ? itemsPerClick : 10000,//fieldDefinition?.def?.type == "TagList" ? Math.min(5000 + catalog.onlineItemCount, 10000) : Math.min(catalog.onlineItemCount || 10000, 10000),//customSort ? catalog.itemCount : itemsPerClick,
            sources: [
              orderField && orderField.field != groupName && !vlp.hierarchy.parseHierarchyGroup(orderField.field) && {[orderFieldName]: {terms: {field: esFieldFor(orderField, true, true), missing_bucket: true, order: firstCustomSort?.orderDesc ? 'asc' : orderBy.order}}},
              {[groupName]: {terms: {field: esField, missing_bucket: true, order: orderField || firstCustomSort?.orderDesc ? 'asc' : orderBy.order}}},
              ...otherGroupedFields.filter(f => _fieldByName[f.field]).map(v => ({[v.field]: {terms: {field: esFieldFor(_fieldByName[v.field], true, false), missing_bucket: v.missing_bucket === undefined || missing_bucket === true, order: firstCustomSort?.orderDesc ? 'asc' : orderBy.order}}}))
            ].filter(_.identity)
          }
        }
      }
    } catch (err) {
      console.error(err)
      debugger
      return {}
    }

    return undefined
  }, {
    equals: (a, b) => stringify(a) === stringify(b)
  })

  labelFor = (tense: 'singular' | 'plural' = 'singular') => this.catalog.labelForFieldName_currentCatalog(this.groupName, tense)

  @action removeFromHierarchy = () => {
    const {isCatalogItemsGrouping, groupBy} = this
    if (isCatalogItemsGrouping) {
      groupBy.clearHierarchy()
    } else {
      groupBy.removeFromHierarchy(this.groupName)
    }
  }

  labelForValue = (value: any) => {
    const {strings, def} = this

    if (def?.type === 'Boolean') {
      return value === 'true' || value === true ? (def.booleanLabels?.trueLabel ?? 'Yes') : (def.booleanLabels?.falseLabel ?? 'No')
    }
    if (value === '') { return strings.emptyLabel}

    return value
  }

  /**
   * Force a call to findAllGroups() if needed
   */
  @computed get findAllGroups_trigger() {
    const {vlpType} = this
    if (vlpType) {
      const {vlp: {hierarchy}} = this
      switch (vlpType.type) {
        case 'plan':
          return hierarchy.selectedPlans
        case 'folder':
          return hierarchy.selectedFolders
        case 'collection':
          return hierarchy.selectedCollections
      }
    }

    return null
  }

  @computed get ancestorForeignKey(): ICatalogFieldConfig {
    if (!this.fieldDefinition) return null

    const {fieldDefinition, catalog} = this,
      {pathToField} = catalog,
      path = pathToField(fieldDefinition),
      fkField = _.findLast(path, p => p.esIndex)

    if (fieldDefinition?.esIndex) return fieldDefinition

    if (!fkField) return null
    else return fkField as ICatalogFieldConfig
  }

  @action findAllGroups = async (parentGroupStore?: GroupByGroupStore) => {
    const {strings, catalog:{stores: {es}, c8}, catalog, def, esField, fieldName, vlp, fieldDefinition, sectionConfig} = this

    if (fieldDefinition?.agg == 'exists') {
      return [] // Handled already in the query manager store
    } else if (fieldDefinition?.field?.startsWith('VLP_')) {
      /*
      If we're within any other groups in the group hierarchy that are part of the VLP hierarchy we need to filter down the items to only those that are valid YFTS-7238
       */
      const vlpHierarchyGroups = parentGroupStore ? parentGroupStore.pathIncludingSelf.filter(p => p.groupName.startsWith('VLP_')) : [],
        {
          vlp: {
            hierarchy: {
              folderById, collectionById, planById, foldersForCollection, selectedPlans, allAvailableCollections, folderByDepthForSelection, selectedFoldersAtDepth,
              selectedFolders, selectedCollections
            }
          }
        } = this

      switch (fieldDefinition.field) {
        case VLP_PLAN_GROUP: {
          const plans : Array<IPlanDefinition> = selectedPlans ?? vlp.hierarchy.plans.plans;

          let r = _.uniqBy(plans.map(p => {
            const id = {[fieldName]: p.id}
            return {key: stringify(id), id, keys: id, title: p.name}
          }), p => p.id)

          if (!_.isEmpty(vlpHierarchyGroups)) {
            r = r.filter(p => {
              return vlpHierarchyGroups.every(({groupName}) => {
                const id = parentGroupStore.props.info.keys[groupName]
                switch (groupName) {
                  case VLP_COLLECTION_GROUP:
                    return collectionById[id].plan.exId === p.keys[fieldName]

                  case VLP_FOLDER_GROUP:
                    return folderById[id].plan.exId === id

                  default:
                    throw new Error(`Unhandled case ${groupName}`)
                }
              })
            })
          }

          return r
        }

        case VLP_COLLECTION_GROUP: {
          let r = _.uniqBy((_.isEmpty(selectedCollections) ? allAvailableCollections : selectedCollections).map(c => {
            const id = {[fieldName]: c.id}

            return {key: stringify(id), id, keys: id, title: c.name}
          }), c => c.id) as Array<{key, id, keys: {}, title}>

          if (!_.isEmpty(vlpHierarchyGroups)) {
            r = r.filter(c => {
              const collection = collectionById[c.keys[fieldName]],
                folders = foldersForCollection[collection.exId]

              return vlpHierarchyGroups.every(({groupName}) => {
                const id = parentGroupStore.props.info.keys[groupName]
                switch (groupName) {
                  case VLP_PLAN_GROUP:
                    return collection.plan.exId === id

                  case VLP_FOLDER_GROUP:
                    return folders.some(f => f.exId === id)

                  default:
                    throw new Error(`Unhandled case ${groupName}`)
                }
              })
            })
          }

          return r
        }

        case VLP_FOLDER_GROUP:
        default: {
          if (!fieldDefinition.field.startsWith(VLP_FOLDER_GROUP)) throw new Error(`Unknown special group ${fieldDefinition.field}`)
          const folderDepth = parseInt(fieldDefinition.field.substring(VLP_FOLDER_GROUP.length + 1)),
            allFoldersAtLevel = folderByDepthForSelection[folderDepth]

          let r = _.uniqBy(allFoldersAtLevel.map(f => {  // .filter(f => _.isEmpty(selectedFoldersAtDepth) || selectedFoldersAtDepth[f.id])  YFTS-9274 - we want ALL groups
            const id = {[fieldName]: f.id}

            return {key: stringify(id), id, keys: id, title: f.name}
          }), (f: any) => f.key)  //title)  YFTS - 9273

          if (!_.isEmpty(vlpHierarchyGroups)) {
            r = r.filter(f => {
              const folder = folderById[f.keys[fieldName]]

              return vlpHierarchyGroups.every(({groupName}) => {
                const id = parentGroupStore.props.info.keys[groupName]
                switch (groupName) {
                  case VLP_PLAN_GROUP:
                    return folder.plan.exId === id

                  case VLP_COLLECTION_GROUP:
                    return collectionById[id].folder.exId === folder.exId

                  default:
                    throw new Error(`Unhandled case ${groupName}`)
                }
              })
            })
          }

          return r
        }
      }
    } else if (def?.type == 'Enum' && def.enumType) {
      const enumValues = await c8.getEnumValues(fieldDefinition)
      //console.log(enumValues)
      return enumValues.map(({name, description}) => ({key: stringify({[fieldName]: name}), id: name, keys: {[fieldName]: name}, title: description}))
    } else {
      const {ancestorForeignKey, associatedWorkspace} = this
      let fkField = ancestorForeignKey && esField.startsWith(`${ancestorForeignKey.field}.`) ? esField.substring(ancestorForeignKey.field.length + 1) : esField

      const termsQuery = {
        terms: {
          field: fkField,
          size: MAX_QUERY_SIZE
        }
      }

      let {aggregations: {all}} = await es.search({
        index: ancestorForeignKey?.esIndex ?? catalog.index,
        meta: 'GroupByGrouping.findAllGroups',
        body: {
          size: 0,
          aggs: {
            all: sectionConfig?.path ? {
              nested: {path: sectionConfig.path},
              aggs: {all: termsQuery}
            } : termsQuery
          }
        }
      }, {log: {query: false}})

      if (all?.all) {
        all = all.all
      }

      const {buckets} = all
      if (this.isEnum) {
        const enumVals = await catalog.c8.getEnumValues(fieldDefinition)
        return enumVals?.map(({description}) => ({key: stringify({[fieldName]: description}), id: description, keys: {[fieldName]: description}, title: description})) ?? []
      }

      if (def?.type === 'String' || def?.type === 'Int' || def?.type === 'Float') {
        return [{key: stringify({[fieldName]: null}), id: null, keys: {[fieldName]: null}, title: _.startCase(UNASSIGNED)}]
      }

      if (def?.type == 'Boolean' && buckets.length === 1) {
        return [
          {key: stringify({[fieldName]: 'true'}), id: 'true', keys: {[fieldName]: 'true'}, title: def?.booleanLabels?.[`trueLabel`]},
          {key: stringify({[fieldName]: 'false'}), id: 'false', keys: {[fieldName]: 'false'}, title: def?.booleanLabels?.[`falseLabel`]}
        ]
      }
      if (def?.type === 'TagList' && !buckets.length) {
        return [{key: stringify({[fieldName]: strings.noValue}), id: {[fieldName]: null}, keys: {[fieldName]: null}, title: _.startCase(UNASSIGNED)}]
      }

      // console.log(buckets)
      let r = buckets.map(b => {
        if (def?.type === 'Boolean' || fieldDefinition?.agg === 'exists') {
          const label = def?.booleanLabels?.[`${b.key_as_string}Label`] ?? b.key
          return {key: stringify({[fieldName]: b.key_as_string}), id: b.key_as_string, keys: {[fieldName]: b.key_as_string}, title: label}
        } else {
          return {key: stringify({[fieldName]: b.key}), id: b.key, keys: {[fieldName]: b.key}, title: b.key}
        }
      })

      if (fieldDefinition?.group?.filterResultBuckets) {
        r = fieldDefinition.group.filterResultBuckets(r, this.catalog.stores)
      }

      return r
    }
  }

  @action applyCustomSortValues = (uniq) => {
    if (this.isCatalogItemsGrouping) return uniq

    const {catalog, fieldDefinition} = this,
      orderBy = _.first(this.orderBy),
      {_fieldByName} = catalog,
      fieldDef = _fieldByName[orderBy.field] ?? fieldDefinition,
      r = fieldDef?.sort?.custom?._key,
      ascOrder = _.cloneDeep(r?.order),
      sequence = orderBy?.custom && (orderBy?.order == ORDER.DESC ? (r?.orderDesc ?? (ascOrder && ascOrder?.reverse())) : ascOrder)

    if (sequence && uniq.length) {
      const sorted = _.sortBy(uniq, (name) => {
        const index = sequence.indexOf(name),
          r = index >= 0 ? index : sequence.length + 1

        return r
      })

      return sorted?.length && sorted
    }
    return uniq
  }

  @action applyCustomSort = (buckets) => {
    if (this.isCatalogItemsGrouping) return buckets

    const {vlp, catalog, fieldName, fieldDefinition, vlpType} = this,
      orderBy = _.first(this.orderBy),
      {_fieldByName} = catalog,
      fieldDef = _fieldByName[orderBy?.field] ?? fieldDefinition,
      r = fieldDef?.sort?.custom?._key,
      ascOrder = _.cloneDeep(r?.order),
      sequence = orderBy?.custom && (orderBy?.order == ORDER.DESC ? (r?.orderDesc ?? (ascOrder && ascOrder?.reverse())) : ascOrder)

    if (sequence && buckets.length) {
      const sorted = _.sortBy(buckets, ({keys, key, name}) => {
        const v = name ?? _.get((keys ?? key), fieldName),
          index = sequence.indexOf(v),
          r = index >= 0 ? index : sequence.length + 1

        return r
      })

      return sorted?.length && sorted
    }

    // VLP Hierarchy Fields can be sorted by custom subtitles like FOLDER_0
    if (vlpType) {
      const vlpSortField = vlp.hierarchy.parseHierarchyGroup(orderBy?.field)

      if (vlpSortField) {
        const {hierarchy: {folderById, foldersForCollection, itemOfType}} = vlp

        buckets = _.orderBy(buckets, b => {
          if (!b.key) {
            return _.toLower(b.name ?? b.id)
          }

          const id = b.key[fieldName]
          const item = itemOfType(vlpType.type, id, null)
          if (item) {
            if (vlpType.type === 'collection') {
              const collection = item as IPlanCollection

              switch (vlpSortField.type) {
                case 'plan':
                  return _.toLower(collection.plan.name)
                case 'collection':
                  return _.toLower(collection.name)

                case 'folder':
                  const r = foldersForCollection[collection.id][vlpSortField.folderDepth]?.name
                  return _.toLower(r)
              }
            } else if (vlpType.type === 'plan') {
              const collection = item as IPlanDefinition
              return _.toLower(collection.name)
            } else if (vlpType.type === 'folder') {
              let folder = item as IPlanSubCategory

              switch (vlpSortField.type) {
                case 'collection':
                  throw new Error()

                case 'plan':
                  return _.toLower(folder.plan.name)

                case 'folder':
                  while (folder.parent && folder.level != vlpSortField.folderDepth) {
                    folder = folderById[folder.parent.id]
                  }
                  return _.toLower(folder.name)
              }
            }
          }

          return 0
        }, orderBy?.order === ORDER.DESC ? 'desc' : 'asc')
      }
    }

    return buckets
  }

  @computed get library() {
    const {fieldDefinition, catalog} = this

    if (fieldDefinition?.esIndex) {
      return catalog.libraryByIndex[fieldDefinition.esIndex]
    }
  }

  deserializeGroupSettings = (settings: Partial<IIndividualGroupSettings>) => {
    const {groupBy} = this,
      replacementSettings = _.keys(settings).reduce((acc, k) => Object.assign(acc, {[k]: {..._.cloneDeep(groupBy.individualGroupSettings), ...settings[k]}}), {})
    // console.log({groupName, replacementSettings})
    if (!_.isEmpty(settings)) {
      if (jsondiff.diff(toJS(this.settingsByGroup), replacementSettings)) {
        this.settingsByGroup.replace(replacementSettings)
      }
    }
  }

  @computed get isAllowedToDragDropAssign() {
    return this.fieldDefinition?.group?.allowDragDropAssign !== false
  }

  @action reset = () => {
    const {groupBy, catalog} = this
    if (this === groupBy.catalogItemsGroup) {
      this.orderBy.replace([{field: catalog.catalogConfig?.initialSort ?? 'name', order: 'asc', custom: false}])
    }
  }

  /** todo - deprecate in favor of swatch definitions **/
  @computed get hasSubGroup() {
    return this.settingsForLevel.heroCardField != null || this.groupDefinition?.subGroup?.field != null
  }

  @action clearSwatches = () => {
    this.settingsForLevel.swatches = []
  }

  nodeFor = (groupId: string) => {
    const {nodes, groupName} = this

    if (!groupId) {
      groupId = stringify({[groupName]: null})
    }

    return nodes.get(groupId)
  }
}

export type GroupOrientation = 'horizontal' | 'vertical'


// WEBPACK FOOTER //
// ./src/stores/catalog/groupBy/GroupByGrouping.tsx